/**
 * Presentation helpers for `.ics` meeting invites shown in the email arche.
 *
 * Everything in an invite is SENDER-controlled. The body already renders the
 * sender's links; the invite card adds a prominent "Join" button, which is a
 * stronger phishing lure than an inline link — so it is only offered for https
 * URLs on a known conferencing host. Anything else stays in the message body,
 * where the user sees it in context.
 */

import type { CalendarInviteEventDto, EmailCalendarInviteAddResult } from '@/lib/api';

/** A meeting invite attachment: `text/calendar` (any params) or a `.ics` name. */
export function isCalendarInvite(mimeType: string, filename?: string): boolean {
  const base = mimeType.toLowerCase().split(';')[0].trim();
  return base === 'text/calendar' || (!!filename && /\.ics$/i.test(filename.trim()));
}

/** Hosts (and their subdomains) whose meeting links earn a Join button. */
const CONFERENCING_HOSTS = [
  'teams.microsoft.com',
  'teams.live.com',
  'zoom.us',
  'zoomgov.com',
  'meet.google.com',
  'webex.com',
  'gotomeeting.com',
  'chime.aws',
  'whereby.com',
] as const;

/** Vendor properties that carry the meeting's join URL, in preference order. */
const JOIN_URL_PROPERTIES = [
  'X-MICROSOFT-SKYPETEAMSMEETINGURL',
  'X-GOOGLE-CONFERENCE',
  'X-MICROSOFT-ONLINEMEETINGEXTERNALLINK',
] as const;

const URL_IN_TEXT = /https:\/\/[^\s<>"')\]]+/g;

export interface MeetingJoinLink {
  url: string;
  /** Display name of the service, e.g. "Microsoft Teams". */
  service: string;
}

function serviceName(host: string): string {
  if (host.endsWith('teams.microsoft.com') || host.endsWith('teams.live.com')) {
    return 'Microsoft Teams';
  }
  if (host.endsWith('zoom.us') || host.endsWith('zoomgov.com')) return 'Zoom';
  if (host === 'meet.google.com') return 'Google Meet';
  if (host.endsWith('webex.com')) return 'Webex';
  if (host.endsWith('gotomeeting.com')) return 'GoTo Meeting';
  if (host.endsWith('chime.aws')) return 'Amazon Chime';
  return 'Whereby';
}

/** The URL as a join link when it is https on a known conferencing host. */
export function asMeetingJoinLink(candidate: unknown): MeetingJoinLink | null {
  if (typeof candidate !== 'string') return null;
  let parsed: URL;
  try {
    parsed = new URL(candidate.trim());
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' || parsed.username || parsed.password) return null;
  const host = parsed.hostname.toLowerCase();
  const known = CONFERENCING_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  return known ? { url: parsed.href, service: serviceName(host) } : null;
}

/**
 * The meeting's join link: a vendor join-URL property first, then the event
 * URL, then the first conferencing link in the location or description (Teams
 * and Zoom put the join link in the description body).
 */
export function findMeetingJoinLink(event: CalendarInviteEventDto): MeetingJoinLink | null {
  for (const key of JOIN_URL_PROPERTIES) {
    const link = asMeetingJoinLink(event.metadata?.[key]);
    if (link) return link;
  }
  const fromUrl = asMeetingJoinLink(event.url);
  if (fromUrl) return fromUrl;
  for (const text of [event.location, event.description]) {
    for (const match of text?.match(URL_IN_TEXT) ?? []) {
      const link = asMeetingJoinLink(match);
      if (link) return link;
    }
  }
  return null;
}

/**
 * The event's date range in the VIEWER's time zone, with the zone named so a
 * cross-zone invite is unambiguous ("Thu, Oct 1, 2026, 2:00 PM – 2:30 PM EDT").
 * All-day events show the date only (they carry no zone).
 */
export function formatInviteWhen(startAt: string, endAt: string, isAllDay?: boolean): string {
  const start = new Date(startAt);
  const end = new Date(endAt);
  if (Number.isNaN(start.getTime())) return '';
  if (isAllDay) {
    // All-day values are UTC-midnight anchored calendar dates — format in UTC
    // so a viewer west of Greenwich doesn't see the previous day.
    const dateOnly: Intl.DateTimeFormatOptions = { dateStyle: 'full', timeZone: 'UTC' };
    const startStr = start.toLocaleDateString(undefined, dateOnly);
    // DTEND is exclusive for all-day events: a one-day event ends next midnight.
    const lastDay = new Date(end.getTime() - 1);
    if (Number.isNaN(end.getTime()) || lastDay.getTime() <= start.getTime()) return startStr;
    const endStr = lastDay.toLocaleDateString(undefined, dateOnly);
    return endStr === startStr ? startStr : `${startStr} – ${endStr}`;
  }
  const dateTime: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  // An inverted range (malformed invite) shows the start only.
  if (Number.isNaN(end.getTime()) || end.getTime() < start.getTime()) {
    return start.toLocaleString(undefined, { ...dateTime, timeZoneName: 'short' });
  }
  const sameDay = start.toDateString() === end.toDateString();
  const startStr = start.toLocaleString(undefined, dateTime);
  // Same day: "Thu, Oct 1, 2026, 2:00 PM – 2:30 PM EDT"; otherwise both ends
  // carry their date. The zone name goes on the end so it reads once.
  const endStr = sameDay
    ? end.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      })
    : end.toLocaleString(undefined, { ...dateTime, timeZoneName: 'short' });
  return `${startStr} – ${endStr}`;
}

/**
 * Toast text for an Add-to-calendar result. Names the target when events were
 * added, and where they ALREADY are when they were skipped — dedup spans every
 * calendar, so "already there" may be a different calendar than the one picked.
 * Mirrors the server's describeInviteAdd (the Fias AI summary).
 */
export function describeInviteAddResult(r: EmailCalendarInviteAddResult): string {
  const already = r.alreadyOn && r.alreadyOn.length > 0 ? r.alreadyOn.join(', ') : 'your calendar';
  if (r.added === 0) return `Already on ${already}`;
  const addedText = `Added ${r.added} event${r.added === 1 ? '' : 's'} to ${
    r.calendarName ?? 'your calendar'
  }`;
  return r.skipped > 0 ? `${addedText} (${r.skipped} already on ${already})` : addedText;
}
